import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CaseManagementReportList = ({ data = [], filters = {}, isLoading = false }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Filtering
  const filteredData = data.filter((row) => {
    const term = searchQuery.toLowerCase();
    return (
      (row.client_name || "").toLowerCase().includes(term) ||
      (row.client_id || "").toLowerCase().includes(term) ||
      (row.case_manager || "").toLowerCase().includes(term) ||
      (row.call_type || "").toLowerCase().includes(term) ||
      (row.interaction_type || "").toLowerCase().includes(term)
    );
  });

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const start = totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Checkbox helpers
  const checkStatus = (statusArray, value) => {
    if (!statusArray || !Array.isArray(statusArray)) return false;
    return statusArray.some(s => s.toLowerCase() === value.toLowerCase());
  };

  // Schedule helpers
  const getScheduleDate = (row, serviceType) => {
    return row.service_type === serviceType ? row.service_schedule_date || "—" : "—";
  };

  const getScheduleFrequency = (row, serviceType) => {
    return row.service_type === serviceType ? row.service_frequency || "—" : "—";
  };

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3", // Using A3 landscape to fit 20 columns beautifully
    });

    doc.setFontSize(18);
    doc.text("Case Management Report", 14, 15);

    const headers = [
      [
        { content: "Client & Interaction Details", colSpan: 7, styles: { fillColor: [13, 92, 117], halign: "center" } },
        { content: "Employment / Occupational Status", colSpan: 6, styles: { fillColor: [46, 91, 136], halign: "center" } },
        { content: "Review Schedule", colSpan: 1, styles: { fillColor: [107, 33, 168], halign: "center" } },
        { content: "Service Schedules", colSpan: 6, styles: { fillColor: [6, 95, 70], halign: "center" } }
      ],
      [
        "Client ID", "Client Name", "Case Manager", "Call Type", "Interaction Type", "Calling Date", "Remarks",
        "Working", "Studying", "Looking for Job", "Never Employed", "Family Business", "Managing Household",
        "Review Freq.",
        "OPD Date", "OPD Freq.", "Home Date", "Home Freq.", "PRP Date", "PRP Freq."
      ]
    ];

    const body = filteredData.map((row) => {
      const occ = row.occupational_status || [];
      return [
        row.client_id || "—",
        row.client_name || "—",
        row.case_manager || "—",
        row.call_type || "—",
        row.interaction_type || "—",
        row.calling_date || "—",
        row.remarks || "—",
        checkStatus(occ, "Working") ? "Yes" : "No",
        checkStatus(occ, "Studying") ? "Yes" : "No",
        checkStatus(occ, "Looking for a Job") ? "Yes" : "No",
        checkStatus(occ, "Never Employed") ? "Yes" : "No",
        checkStatus(occ, "Inducted in Family Business") ? "Yes" : "No",
        checkStatus(occ, "Managing Household") ? "Yes" : "No",
        row.review_frequency || "—",
        getScheduleDate(row, "OPD"),
        getScheduleFrequency(row, "OPD"),
        getScheduleDate(row, "Home Visit"),
        getScheduleFrequency(row, "Home Visit"),
        getScheduleDate(row, "PRP"),
        getScheduleFrequency(row, "PRP")
      ];
    });

    autoTable(doc, {
      head: headers,
      body: body,
      startY: 22,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [60, 60, 60],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    doc.save("case_management_report.pdf");
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = [
      "Client ID", "Client Name", "Case Manager", "Call Type", "Interaction Type", "Calling Date", "Remarks",
      "Working", "Studying", "Looking for Job", "Never Employed", "Family Business", "Managing Household",
      "Review Frequency",
      "OPD Schedule Date", "OPD Frequency",
      "Home Visit Schedule Date", "Home Visit Frequency",
      "PRP Schedule Date", "PRP Frequency"
    ];

    const csvRows = [headers.join(",")];

    filteredData.forEach((row) => {
      const occ = row.occupational_status || [];
      const values = [
        `"${row.client_id || ""}"`,
        `"${row.client_name || ""}"`,
        `"${row.case_manager || ""}"`,
        `"${row.call_type || ""}"`,
        `"${row.interaction_type || ""}"`,
        `"${row.calling_date || ""}"`,
        `"${(row.remarks || "").replace(/"/g, '""')}"`,
        `"${checkStatus(occ, "Working") ? "Yes" : "No"}"`,
        `"${checkStatus(occ, "Studying") ? "Yes" : "No"}"`,
        `"${checkStatus(occ, "Looking for a Job") ? "Yes" : "No"}"`,
        `"${checkStatus(occ, "Never Employed") ? "Yes" : "No"}"`,
        `"${checkStatus(occ, "Inducted in Family Business") ? "Yes" : "No"}"`,
        `"${checkStatus(occ, "Managing Household") ? "Yes" : "No"}"`,
        `"${row.review_frequency || ""}"`,
        `"${getScheduleDate(row, "OPD")}"`,
        `"${getScheduleFrequency(row, "OPD")}"`,
        `"${getScheduleDate(row, "Home Visit")}"`,
        `"${getScheduleFrequency(row, "Home Visit")}"`,
        `"${getScheduleDate(row, "PRP")}"`,
        `"${getScheduleFrequency(row, "PRP")}"`
      ];
      csvRows.push(values.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "case_management_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="card-body p-0">
      {/* Table Toolbar */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><button className="dropdown-item" onClick={handleExportCSV}><i className="ri-file-excel-line me-1 text-success"></i> CSV</button></li>
            </ul>
          </div>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search report..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: "200px" }}
          />
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">Showing {start}-{end} of {totalRows} records</h6>
        <div className="d-flex align-items-center gap-2">
          <label className="small text-muted mb-0">Show</label>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Grid Container */}
      <div className="table-responsive" style={{ maxHeight: "650px", overflowX: "auto" }}>
        <table className="table table-bordered table-striped align-middle mb-0" style={{ minWidth: "2200px" }}>
          <thead className="table-light text-center">
            {/* Top Level Group Headers */}
            <tr>
              <th colSpan="7" className="fw-semibold py-2">
                Client &amp; Interaction Details
              </th>
              <th colSpan="6" className="fw-semibold py-2">
                Employment / Occupational Status
              </th>
              <th colSpan="1" className="fw-semibold py-2">
                Review Schedule
              </th>
              <th colSpan="6" className="fw-semibold py-2">
                Service Schedules
              </th>
            </tr>
            {/* Detail Headers */}
            <tr>
              <th className="py-2" style={{ width: "120px" }}>Client ID</th>
              <th className="py-2" style={{ width: "180px" }}>Client Name</th>
              <th className="py-2" style={{ width: "180px" }}>Case Manager</th>
              <th className="py-2" style={{ width: "120px" }}>Call Type</th>
              <th className="py-2" style={{ width: "140px" }}>Interaction Type</th>
              <th className="py-2" style={{ width: "120px" }}>Calling Date</th>
              <th className="py-2" style={{ width: "250px" }}>Remarks</th>
              <th className="py-2" style={{ width: "100px" }}>Working</th>
              <th className="py-2" style={{ width: "100px" }}>Studying</th>
              <th className="py-2" style={{ width: "130px" }}>Looking for a Job</th>
              <th className="py-2" style={{ width: "120px" }}>Never Employed</th>
              <th className="py-2" style={{ width: "180px" }}>Family Business</th>
              <th className="py-2" style={{ width: "150px" }}>Managing Household</th>
              <th className="py-2" style={{ width: "140px" }}>Review Frequency</th>
              <th className="py-2" style={{ width: "140px" }}>OPD Date</th>
              <th className="py-2" style={{ width: "120px" }}>OPD Frequency</th>
              <th className="py-2" style={{ width: "140px" }}>Home Visit Date</th>
              <th className="py-2" style={{ width: "140px" }}>Home Visit Freq.</th>
              <th className="py-2" style={{ width: "140px" }}>PRP Date</th>
              <th className="py-2" style={{ width: "120px" }}>PRP Frequency</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="20" className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="20" className="text-center py-4 text-muted">
                  No records found
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => {
                const occ = row.occupational_status || [];
                return (
                  <tr key={row.id}>
                    <td className="font-monospace fw-semibold">{row.client_id}</td>
                    <td className="fw-semibold text-dark">{row.client_name}</td>
                    <td>{row.case_manager || "—"}</td>
                    <td>
                      <span className="badge bg-label-info text-capitalize">
                        {row.call_type || "—"}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-label-secondary">
                        {row.interaction_type || "—"}
                      </span>
                    </td>
                    <td>{row.calling_date || "—"}</td>
                    <td className="text-wrap small">{row.remarks || "—"}</td>
                    <td className="text-center">
                      {checkStatus(occ, "Working") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="text-center">
                      {checkStatus(occ, "Studying") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="text-center">
                      {checkStatus(occ, "Looking for a Job") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="text-center">
                      {checkStatus(occ, "Never Employed") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="text-center">
                      {checkStatus(occ, "Inducted in Family Business") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="text-center">
                      {checkStatus(occ, "Managing Household") ? (
                        <span className="badge bg-success bg-xs">Yes</span>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td>{row.review_frequency || "—"}</td>
                    <td>{getScheduleDate(row, "OPD")}</td>
                    <td>{getScheduleFrequency(row, "OPD")}</td>
                    <td>{getScheduleDate(row, "Home Visit")}</td>
                    <td>{getScheduleFrequency(row, "Home Visit")}</td>
                    <td>{getScheduleDate(row, "PRP")}</td>
                    <td>{getScheduleFrequency(row, "PRP")}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalRows > 0 && (
        <div className="d-flex justify-content-between align-items-center p-4 flex-wrap gap-3 border-top">
          <div className="small text-muted">
            Showing {start} to {end} of {totalRows} entries
          </div>

          <div className="d-flex gap-1">
            <button
              className="btn btn-xs btn-label-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`btn btn-xs ${currentPage === index + 1 ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            <button
              className="btn btn-xs btn-label-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseManagementReportList;
